import { PropsWithChildren, useMemo } from 'react';

import { NetworkCostOverTimeContext } from 'features/cluster/cost-report/providers/NetworkCostOverTimeProvider';
import { useCostReportContext } from 'features/cost-report/hooks/useCostReportContext';
import { aggregateDataTransferDatapoints } from 'features/cost-report/utils/networkCosts';
import { useWorkloadDataTransferCostQuery } from 'hooks/queries/cost';
import { fillGaps } from 'utils/metrics';

import { WorkloadDetailsRouteProps } from '../../_types';

import { useCluster } from '@cast/shared-cluster-context-data-access';

//prettier-ignore
const dynamicCopiesMap = {
  summary: {
    totalSpendTooltip: 'Total cluster network (egress and ingress) spend for the selected period',
    averageTotalDailyCost: 'Average daily network cost',
    averageDailyCost: 'Average daily network cost by ingress and egress traffic',
  },
  dailyDetails: {
    totalCost: 'Sum of egress and ingress traffic cost. Typically, ingress does not incur any costs, depending on the cloud provider',
  },
};

export const WorkloadNetworkCostOverTimeProvider = ({
  children,
  workloadName,
  namespace,
  workloadType,
}: PropsWithChildren<WorkloadDetailsRouteProps>) => {
  const { cluster } = useCluster();
  const {
    isDefaultFilter,
    isEgressdInstalled,
    isKvisorInstalled,
    isCapabilitiesLoading,
    capabilitiesError,
    refetchCapabilities,
    currentDateRange,
    adjustedDateRange,
  } = useCostReportContext();

  const [previousFrom, previousTo] = useMemo(
    () => [
      adjustedDateRange[0].subtract(1, 'M'),
      adjustedDateRange[1].subtract(1, 'M'),
    ],
    [adjustedDateRange]
  );

  const {
    isLoading: isLoadingCurrent,
    data: currentData,
    error: currentError,
    refetch: currentRefetch,
  } = useWorkloadDataTransferCostQuery({
    clusterId: cluster.id,
    workload: workloadName,
    namespace,
    workloadType,
    startTime: adjustedDateRange[0].toISOString(),
    endTime: adjustedDateRange[1].toISOString(),
    stepSeconds: 86400,
    enabled: isEgressdInstalled || isKvisorInstalled,
  });

  const { isLoading: isLoadingPrevious, data: previousData } =
    useWorkloadDataTransferCostQuery({
      clusterId: cluster.id,
      workload: workloadName,
      namespace,
      workloadType,
      startTime: previousFrom.toISOString(),
      endTime: previousTo.toISOString(),
      stepSeconds: 86400,
      enabled: isDefaultFilter && (isEgressdInstalled || isKvisorInstalled),
    });

  const currentTimeSeriesData = useMemo(() => {
    return aggregateDataTransferDatapoints(
      currentData?.item ? [currentData.item] : []
    );
  }, [currentData?.item]);

  const previousTimeSeriesData = useMemo(() => {
    return aggregateDataTransferDatapoints(
      previousData?.item ? [previousData.item] : []
    );
  }, [previousData?.item]);

  const currentChartTimeSeriesData = useMemo(() => {
    return fillGaps({
      timeSeries: currentTimeSeriesData,
      dateRange: {
        from: currentDateRange[0],
        to: currentDateRange[1],
      },
      filler: {},
      unit: 'day',
    });
  }, [currentDateRange, currentTimeSeriesData]);

  return (
    <NetworkCostOverTimeContext.Provider
      value={{
        key: 'workload',
        error: currentError || capabilitiesError,
        refetch: () => {
          currentRefetch();
          refetchCapabilities();
        },
        current: {
          isLoading: isLoadingCurrent || isCapabilitiesLoading,
          data: {
            items: currentTimeSeriesData,
          },
          chartData: currentChartTimeSeriesData,
        },
        previous: {
          isLoading: isLoadingPrevious || isCapabilitiesLoading,
          data: {
            items: previousTimeSeriesData,
          },
          chartData: [],
        },
        dynamicCopiesMap,
      }}
    >
      {children}
    </NetworkCostOverTimeContext.Provider>
  );
};
